import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./Terms.scss";
import DynamicHeader from "../../Header/DynamicHeader";

const Terms = () => {
  const navigate = useNavigate();

  const SUPPORT_EMAIL =
    process.env.REACT_APP_SUPPORT_EMAIL || "support@cvanto.com";
  const PUBLIC_BASE_URL =
    process.env.REACT_APP_PUBLIC_BASE_URL || "";

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, []);

  // IntersectionObserver
  const rootRef = useRef(null);
  useEffect(() => {
    const rootEl = rootRef.current;
    if (!rootEl) return;

    const items = Array.from(rootEl.querySelectorAll(".js-reveal"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            observer.unobserve(e.target);
          }
        });
      },
      { root: null, rootMargin: "0px 0px -10% 0px", threshold: 0.1 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>Правила спільноти — Cvanto</title>
        <meta name="description" content="Правила спільноти та умови використання маркетплейсу Cvanto." />
        <link rel="canonical" href="https://cvanto.com/terms" />
      </Helmet>

      {/* */}
      <DynamicHeader
        title="Правила спільноти"
        variant="glass"
        size="regular"
        showBack
        withSpacer
      />

      <div className="legal legal--terms" ref={rootRef}>
        {/* */}
        <header className="legal__hero js-reveal" data-reveal="hero">
          {/* */}

          <div className="legal__hero-body">
            <h1 className="legal__title">Умови користування та Правила спільноти Cvanto</h1>

            {PUBLIC_BASE_URL && (
              <p className="legal__subtitle">
                Платформа: <u>{PUBLIC_BASE_URL}</u>
              </p>
            )}
          </div>
        </header>

        {/* */}
        <nav className="legal__toc js-reveal" aria-label="Зміст">
          <strong>Зміст</strong>
          <ol>
            <li><a href="#intro">Преамбула та прийняття умов</a></li>
            <li><a href="#conduct">Правила поведінки спільноти</a></li>
            <li><a href="#prohibited">Заборонений та обмежений контент</a></li>
            <li><a href="#ugc">Контент користувача та права Cvanto</a></li>
            <li><a href="#moderation">Модерація, санкції та оскарження</a></li>
            <li><a href="#commerce">Оголошення, угоди та заборонені товари/послуги</a></li>
            <li><a href="#payments">Платежі та відповідальність сторін</a></li>
            <li><a href="#ads-wallet">Реклама, Cvanto Coin та відсутність повернення коштів</a></li>
            <li><a href="#ip">Інтелектуальна власність та процедура повідомлення</a></li>
            <li><a href="#liability">Відмова від гарантій та обмеження відповідальності</a></li>
            <li><a href="#age">Вікові обмеження</a></li>
            <li><a href="#termination">Припинення доступу та видалення акаунта</a></li>
            <li><a href="#law">Застосовне право та юрисдикція</a></li>
            <li><a href="#changes">Зміни до Умов</a></li>

            {/* Нові розділи для «профі»-рівня та підписки */}
            <li><a href="#platform-rules">Крос-платформені заборони (антиспам, насильство, шахрайство)</a></li>
            <li><a href="#premium">Cvanto Premium: умови підписки</a></li>
            <li><a href="#premium-billing">Оплати, податки, конвертація валют</a></li>
            <li><a href="#premium-cancel">Скасування підписки та повернення коштів</a></li>
            <li><a href="#premium-3p">Платежі третіх сторін (Apple/Google/ін.)</a></li>
            <li><a href="#premium-termination">Розірвання підписки</a></li>
            <li><a href="#premium-variations">Зміни ціни та умов Premium</a></li>
            <li><a href="#premium-scope">Обсяг послуг Premium</a></li>
            <li><a href="#premium-availability">Доступність, сумісність та оновлення ПЗ</a></li>
            <li><a href="#premium-network">Швидкість, реклама та «заряди/буст»</a></li>
            <li><a href="#premium-giveaways">Розіграші, подарункові підписки та прозорість</a></li>
          </ol>
        </nav>

        {/* */}
        <section id="intro" className="legal__section js-reveal">
          <h2>1. Преамбула та прийняття умов</h2>
          <p>
            Ці Умови користування та Правила спільноти (далі — <b>«Умови»</b>) регулюють доступ до онлайн-платформи
            <b> Cvanto</b> (вебсайт{PUBLIC_BASE_URL ? <> <u>{PUBLIC_BASE_URL}</u></> : ""}, мобільні додатки та пов’язані сервіси; далі — <b>«Платформа»</b>)
            та її використання. Платформа адмініструється командою Cvanto (далі — <b>«Адміністрація»</b>).
          </p>
          <p>
            Користуючись Платформою, ви підтверджуєте, що прочитали, зрозуміли і погоджуєтесь із цими Умовами.
            Якщо ви не погоджуєтесь — припиніть користування Платформою. Окремі функції можуть мати додаткові правила,
            що є невід’ємною частиною цих Умов.
          </p>
          <p>
            З питань Умов, модерації або платежів звертайтесь на email:{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </section>

        {/* */}
        <section id="conduct" className="legal__section js-reveal">
          <h2>2. Правила поведінки спільноти</h2>
          <ul>
            <li><b>Повага:</b> заборонені образи, цькування, дискримінація, погрози, розпалювання ворожнечі, doxxing (розкриття приватних даних) та переслідування.</li>
            <li><b>Чесність:</b> не видавайте себе за інших, не використовуйте фейкові облікові записи чи маніпулятивні схеми (боти, накрутки, скоординовані маніпуляції).</li>
            <li><b>Безпека:</b> не розміщуйте шкідливе ПЗ, фішинг, інструкції для зламів, та інші загрози безпеці.</li>
            <li><b>Без спаму:</b> заборонені масові небажані розсилки, повторні нецільові повідомлення, клікбейт, реферальний/партнерський спам.</li>
            <li><b>Заборона шахрайства:</b> будь-які скамерські схеми, піраміди, введення в оману щодо товарів/послуг, фальсифікація документів, підробки — заборонені.</li>
            <li><b>Повага до приватності:</b> не публікуйте чужі персональні дані без правової підстави та згоди суб’єкта.</li>
          </ul>
        </section>

        {/* */}
        <section id="prohibited" className="legal__section js-reveal">
          <h2>3. Заборонений та обмежений контент</h2>
          <ul>
            <li><b>18+ та відвертий контент:</b> порнографія, відверті сексуальні матеріали, сексуалізація неповнолітніх (нульова толерантність, можливе повідомлення органів).</li>
            <li><b>Насильство і заклики:</b> жорстокі сцени, погрози, заклики до насильства, тероризму, екстремізму.</li>
            <li><b>Мова ворожнечі:</b> приниження чи сегрегація за ознаками (раса, етнічність, національність, релігія, стать, гендерна ідентичність, сексуальна орієнтація, інвалідність тощо).</li>
            <li><b>Самопошкодження:</b> пропагування суїциду, самокаліцтва, небезпечних «челенджів».</li>
            <li><b>Наркотики/небезпечні речовини:</b> незаконні наркотики, прекурсори, інструкції з виготовлення.</li>
            <li><b>Зброя/небезпечні предмети:</b> вогнепальна, вибухові речовини, боєприпаси, деталі до них, а також інше, що обмежене законом.</li>
            <li><b>Незаконна діяльність:</b> продаж/купівля краденого, фальшивих документів, особистих даних, піратського ПЗ/контенту, послуги, що порушують закон.</li>
            <li><b>Медичні/фінансові послуги без дозволів:</b> інформація або пропозиції, що потребують спеціальної ліцензії — лише за наявності законних підстав і підтверджень.</li>
            <li><b>Азартні ігри:</b> діяльність, що регулюється/заборонена законом, без відповідних дозволів.</li>
          </ul>
          <p>Порушення цього розділу може призвести до негайного блокування без попередження.</p>
        </section>

        {/* */}
        <section id="ugc" className="legal__section js-reveal">
          <h2>4. Контент користувача та права Cvanto</h2>
          <p>
            Ви несете повну відповідальність за контент, який публікуєте або передаєте через Платформу, і гарантуєте,
            що маєте всі необхідні права. Розміщуючи контент, ви надаєте Cvanto <b>невиключну, безоплатну,
            всесвітню ліцензію</b> на його відтворення, зберігання, публічне показування, обробку та розповсюдження
            в межах Платформи з метою надання та покращення сервісів, у тому числі створення технічних копій і резервних знімків.
          </p>
          <p>
            Видалення вами контенту зазвичай припиняє його публічний показ, однак копії можуть зберігатися у кеші/бек-апах
            протягом розумного строку або там, де контент вже використано іншими користувачами (напр., у чаті).
          </p>
        </section>

        {/* */}
        <section id="moderation" className="legal__section js-reveal">
          <h2>5. Модерація, санкції та оскарження</h2>
          <p>
            Cvanto застосовує комбіновану модерацію (автоматизовану та ручну). Ми можемо без попередження видаляти/приховувати
            контент, обмежувати доступ до функцій, тимчасово або безповоротно блокувати акаунт, скасовувати платні
            послуги/розміщення у разі порушення Умов або закону, а також при обґрунтованій підозрі у шахрайстві.
          </p>
          <p>
            Ви можете подати апеляцію через інтерфейс скарги або на email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            Рішення щодо модерації приймаються на розсуд Cvanto з урахуванням тяжкості та контексту порушення.
          </p>
        </section>

        {/* */}
        <section id="commerce" className="legal__section js-reveal">
          <h2>6. Оголошення, угоди та заборонені товари/послуги</h2>
          <ul>
            <li><b>Достовірність:</b> описи мають бути точними, без приховування дефектів, з реальними фото.</li>
            <li><b>Законність:</b> розміщуйте лише те, що не суперечить законодавству України та місцевим нормам.</li>
            <li><b>Заборонені товари/послуги:</b> зброя, боєприпаси, вибухівка; наркотики/прекурсори; підробки; піратський контент; документи та персональні дані; 
              порнографія, секс-послуги; тварини/біоматеріали та інші категорії, обмежені законом.</li>
            <li><b>Категорійні правила:</b> окремі розділи Платформи можуть містити додаткові вимоги.</li>
          </ul>
        </section>

        {/* */}
        <section id="payments" className="legal__section js-reveal">
          <h2>7. Платежі та відповідальність сторін</h2>
          <p>
            Платформа може пропонувати платні функції/послуги та інтегруватися з платіжними провайдерами.
            Реквізити платіжних карток не зберігаються Cvanto. Угоди між користувачами (купівля-продаж тощо) укладаються
            <b> виключно між ними</b>; Cvanto не є стороною таких угод і не гарантує якість, фактичне існування,
            законність, своєчасність чи відповідність товарів/послуг описам.
          </p>
        </section>

        <section id="ads-wallet" className="legal__section js-reveal">
          <h2>8. Реклама, Cvanto Coin та відсутність повернення коштів</h2>
          <p>
            Cvanto може надавати рекламні інструменти (наприклад: <b>просування оголошень</b>, <b>спонсоровані покази</b>,
            <b>буст/підняття</b>, <b>пакети видимості</b> та інші цифрові послуги). Оплата таких послуг може здійснюватися
            шляхом придбання та використання внутрішньої цифрової одиниці обліку <b>Cvanto Coin</b> (далі — <b>«Цифрова валюта»</b>).
          </p>
          <ul>
            <li>
              <b>Правова природа:</b> Cvanto Coin є <b>умовною одиницею обліку/передплаченою вартістю цифрових послуг</b> у межах Платформи.
              Cvanto Coin <b>не є</b> криптовалютою, електронними грошима, цінним папером або банківським продуктом і <b>не надає</b>
              права на відсотки, дивіденди чи інший фінансовий дохід.
            </li>
            <li>
              <b>Немає виведення:</b> Цифрова валюта призначена <b>виключно</b> для використання всередині Cvanto і <b>не підлягає</b>
              обміну на готівку/безготівкові кошти та/або виведенню з Платформи.
            </li>
            <li>
              <b>Остаточність покупки:</b> Після зарахування Cvanto Coin до вашого балансу операція вважається наданою/виконаною.
              <b>Повернення коштів</b> за придбану Цифрову валюту та/або невикористаний залишок <b>не здійснюється</b>,
              <b>крім</b> випадків, коли повернення <b>прямо вимагається</b> застосовним законом або правилами платіжного провайдера.
            </li>
            <li>
              <b>Рекламний результат:</b> Cvanto <b>не гарантує</b> конкретних результатів реклами (переглядів, лідів, продажів, окупності).
              Покази/ранжування можуть залежати від попиту, сезонності, конкуренції, релевантності, налаштувань і поведінки аудиторії.
            </li>
            <li>
              <b>Модерація реклами:</b> Рекламні матеріали та просувані оголошення мають відповідати Умовам, законам і правилам категорій.
              Cvanto може <b>відхилити</b>, <b>обмежити</b> або <b>зупинити</b> рекламну активність у разі порушень або обґрунтованих підозр
              (наприклад, шахрайство, маніпуляції, заборонені товари/послуги). У таких випадках <b>компенсація</b> може не надаватися,
              якщо інше не передбачено законом.
            </li>
            <li>
              <b>Технічні збої:</b> У разі підтвердженої технічної помилки з боку Cvanto (наприклад, подвійне списання або некоректне списання),
              Адміністрація може на власний розсуд здійснити <b>коригування балансу</b> (повернення Cvanto Coin на внутрішній баланс) або іншу форму
              врегулювання, узгоджену з користувачем, у межах, дозволених законом.
            </li>
          </ul>
          <p>
            Використовуючи рекламні інструменти, ви підтверджуєте, що розумієте: ви купуєте <b>цифрову послугу/доступ</b> та/або
            <b>Цифрову валюту для витрат усередині Платформи</b>, а не фінансовий продукт, і що стандартно <b>повернення коштів не передбачено</b>.
          </p>
        </section>

        {/* */}
        <section id="ip" className="legal__section js-reveal">
          <h2>9. Інтелектуальна власність та процедура повідомлення</h2>
          <p>
            Якщо ви вважаєте, що контент порушує ваші права інтелектуальної власності, надішліть повідомлення на{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> із: (1) посиланням на матеріал; (2) описом прав,
            що порушені; (3) вашими контактами; (4) заявою про добросовісність та достовірність відомостей. Cvanto може
            видалити або обмежити доступ до такого контенту і вжити заходів щодо повторних порушників.
          </p>
        </section>

        {/* */}
        <section id="liability" className="legal__section js-reveal">
          <h2>10. Відмова від гарантій та обмеження відповідальності</h2>
          <p>
            Платформа надається «як є» («as is») без прямих чи неявних гарантій. Cvanto не гарантує безперервність,
            відсутність помилок чи повну безпеку. У межах, дозволених законом, Cvanto та пов’язані особи не несуть 
            відповідальності за непрямі, побічні, штрафні збитки, втрачений прибуток/дані, що виникли внаслідок 
            використання або неможливості використання Платформи.
          </p>
          <p>
            Якщо відповідальність не може бути виключена, вона обмежується сумою, сплаченою Користувачем Cvanto за платні
            послуги протягом останніх 3 (трьох) місяців, але не більше меж, встановлених законом.
          </p>
        </section>

        {/* */}
        <section id="age" className="legal__section js-reveal">
          <h2>11. Вікові обмеження</h2>
          <p>
            Користування Платформою дозволено особам від <b>16 років</b> (або вищого віку, якщо цього вимагає закон
            країни перебування). Для укладання договорів/платежів ви повинні мати повну цивільну дієздатність
            (зазвичай <b>18+</b>).
          </p>
        </section>

        {/* */}
        <section id="termination" className="legal__section js-reveal">
          <h2>12. Припинення доступу та видалення акаунта</h2>
          <p>
            Ми можемо призупинити або припинити доступ до Платформи у разі порушення Умов чи закону. Ви можете 
            видалити акаунт у налаштуваннях. Частина даних може зберігатися протягом строків, визначених законом
            або нашою Політикою конфіденційності.
          </p>
        </section>

        {/* */}
        <section id="law" className="legal__section js-reveal">
          <h2>13. Застосовне право та юрисдикція</h2>
          <p>
            До цих Умов застосовується право України. Спори розглядаються у компетентних судах України відповідно до
            процесуального законодавства. Робоча мова цих Умов — українська.
          </p>
        </section>

        {/* */}
        <section id="changes" className="legal__section js-reveal">
          <h2>14. Зміни до Умов</h2>
          <p>
            Ми можемо періодично оновлювати ці Умови. Нова редакція набирає чинності з моменту публікації на цій сторінці,
            якщо інше не передбачено в самій редакції. Подальше користування Платформою означає вашу згоду з оновленнями.
          </p>
        </section>

        {/* ===== Нові розділи, узгоджені з практикою професійних платформ та з урахуванням специфіки Cvanto ===== */}

        <section id="platform-rules" className="legal__section js-reveal">
          <h2>15. Крос-платформені заборони (антиспам, насильство, шахрайство)</h2>
          <p>
            На Cvanto діють базові заборони, узгоджені з галузевою практикою: <b>заборона спаму</b>, <b>обману користувачів</b>,
            <b> пропаганди насильства</b> та <b>розміщення незаконного порнографічного контенту</b> у відкритих розділах.
            Також заборонена <b>будь-яка діяльність, що визнається незаконною в більшості країн</b> (зокрема жорстоке
            поводження з дітьми, продаж/пропонування незаконних товарів і послуг, підроблені документи тощо).
          </p>
          <p>
            Cvanto залишає за собою право оновлювати ці правила, щоб захистити користувачів і забезпечити стабільність сервісу.
          </p>
        </section>

        <section id="premium" className="legal__section js-reveal">
          <h2>16. Cvanto Premium: умови підписки</h2>
          <p>
            <b>Cvanto Premium</b> — це підписка, яка відкриває ексклюзивні можливості (розширені ліміти, підвищення видимості
            оголошень/каналів, додаткові інструменти для брендів та авторів). Оформлюючи підписку, ви надаєте дозвіл на
            періодичні платежі (щомісячні/щорічні) до моменту скасування вами або Cvanto.
          </p>
          <p>
            Для обробки платежів ми співпрацюємо зі сторонніми провайдерами. Функції Premium можуть відрізнятися залежно від країни/клієнта.
          </p>
        </section>

        <section id="premium-billing" className="legal__section js-reveal">
          <h2>17. Оплати, податки, конвертація валют</h2>
          <ul>
            <li><b>Керування підпискою:</b> у налаштуваннях профілю Cvanto &rarr; Premium.</li>
            <li><b>Відхилення платежу:</b> при відмові у списанні потрібно оновити платіжні реквізити; інакше підписку може бути скасовано.</li>
            <li><b>Поновлення періоду:</b> у разі успішного списання після оновлення реквізитів новий період рахують від <b>планової</b> дати поновлення.</li>
            <li><b>Податки та комісії:</b> локальні податки/курси/комісії платіжних систем можуть застосовуватися та не контролюються Cvanto.</li>
          </ul>
        </section>

        <section id="premium-cancel" className="legal__section js-reveal">
          <h2>18. Скасування підписки та повернення коштів</h2>
          <p>
            Ви можете скасувати підписку будь-коли через налаштування Premium (або в налаштуваннях Apple/Google, якщо покупку
            здійснено через магазини застосунків). Після скасування підписка активна до кінця поточного розрахункового періоду.
            Часткове повернення коштів за «невикористані дні» не передбачено, якщо інше прямо не встановлено законом або
            політикою відповідного магазину застосунків.
          </p>
          <p>
            Видалення акаунта або застосунку не скасовує підписку автоматично — скористайтеся інструкціями для скасування.
          </p>
        </section>

        <section id="premium-3p" className="legal__section js-reveal">
          <h2>19. Платежі третіх сторін (Apple/Google/ін.)</h2>
          <p>
            Якщо ви оформлюєте Premium через третю сторону (Apple App Store, Google Play тощо), можуть діяти додаткові умови
            такої сторони (оплати, повернення, скасування). Розрахунки за такими покупками регулюються вашою угодою з цією стороною.
          </p>
        </section>

        <section id="premium-termination" className="legal__section js-reveal">
          <h2>20. Розірвання підписки</h2>
          <ul>
            <li><b>Порушення Умов:</b> недотримання правил може призвести до блокування акаунта й втрати переваг Premium без компенсації.</li>
            <li><b>Одностороннє припинення:</b> Cvanto може припинити надання Premium (напр., форс-мажор/вимоги закону) з пропорційним поверненням за невикористаний період.</li>
          </ul>
        </section>

        <section id="premium-variations" className="legal__section js-reveal">
          <h2>21. Зміни ціни та умов Premium</h2>
          <p>
            Ми можемо коригувати вартість і умови поновлення підписки. Про суттєві зміни повідомляємо не пізніше ніж за 30 днів
            у вашому акаунті Cvanto, із правом скасувати підписку до набрання нових умов чинності.
          </p>
          <p>
            Офіційні повідомлення щодо Premium надсилаються через верифіковані службові облікові записи/системні сповіщення.
          </p>
        </section>

        <section id="premium-scope" className="legal__section js-reveal">
          <h2>22. Обсяг послуг Premium</h2>
          <p>
            Актуальний перелік можливостей Premium вказано у розділі «Premium» в додатку/на сайті. Ми можемо додавати нові
            можливості або вилучати окремі функції з юридичних/технічних причин. Частина функцій може згодом стати доступною
            безоплатно для всіх.
          </p>
        </section>

        <section id="premium-availability" className="legal__section js-reveal">
          <h2>23. Доступність, сумісність та оновлення ПЗ</h2>
          <ul>
            <li><b>Доступність:</b> можливі тимчасові технічні перебої; Cvanto не компенсує непрямі/прямі збитки від простоїв.</li>
            <li><b>Сумісність клієнтів:</b> не всі клієнти (офіційні/сторонні) можуть одночасно підтримувати весь Premium-функціонал.</li>
            <li><b>Вимоги до версії:</b> для окремих можливостей потрібна остання версія застосунку; у взаємодіях партнерам теж може знадобитися оновлення.</li>
          </ul>
        </section>

        <section id="premium-network" className="legal__section js-reveal">
          <h2>24. Швидкість, реклама та «заряди/буст»</h2>
          <ul>
            <li><b>Швидкість:</b> знімаємо наші обмеження, але фактична швидкість залежить від провайдера, обладнання та мережевих умов.</li>
            <li><b>Реклама:</b> Premium може приховувати частину спонсорованих повідомлень Cvanto в публічних секціях; водночас автори/канали можуть розміщувати власні інтеграції, які Premium не блокує.</li>
            <li><b>«Заряди/буст»:</b> Premium може надавати обмежену кількість «зарядів» для підвищення рівня каналів/спільнот/оголошень (розширення інструментів, лімітів, стилю). Модель «зарядів» може змінюватися (вимоги, ефекти, кількість).</li>
          </ul>
        </section>

        <section id="premium-giveaways" className="legal__section js-reveal">
          <h2>25. Розіграші, подарункові підписки та прозорість</h2>
          <p>
            Адміністратори можуть купувати підписки Premium для розіграшів серед підписників, отримуючи «заряди/буст».
            Умови розіграшу визначає адміністратор. Cvanto проводить відбір переможців прозоро за оголошеними критеріями.
            Результати остаточні; у випадку технічної помилки Cvanto докладає зусиль для виправлення, без гарантій для організаторів/учасників.
          </p>
          <ul>
            <li><b>Коди/посилання:</b> переможець отримує унікальне посилання/код для активації; «заряди» можуть зараховуватися каналу/спільноті, що організували розіграш.</li>
            <li><b>Різниця в цінах:</b> покупки можливі через різні клієнти/платформи; вартість може відрізнятися; усі покупки — без повернення, якщо інше не вимагає закон/сторона-агрегатор.</li>
            <li><b>Комлаєнс:</b> організатор повністю відповідає за дотримання локального законодавства щодо промоакцій/лотерей/розіграшів.</li>
            <li><b>Зловживання:</b> канали/сторінки, що вводять в оману (напр., стимулюють приєднання, а згодом роблять «чарджбек»), можуть бути обмежені в пошуку/позначені як шахрайські; «фейкові» підписники можуть бути видалені.</li>
          </ul>
        </section>

        {/* */}
      </div>
    </>
  );
};

export default Terms;
